import { ICartProps } from '../../../../contexts/CartContext/CartContext.types';
import {
    CartWorkflowStepValidationResults,
    DefaultCartWorkflowStepValidationResults
} from '../../CartWorkflow.types';

const zipcodeRegex = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
const emailAddressRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
const phoneNumberRegex = /^[+]?[(]?\d{3}[)]?[-\s.]?\d{3}[-\s.]?\d{4,6}$/;
const poBoxRegex =
    /^ *((#\d+)|((box|bin)[-. \/\\]?\d+)|(.*p[ \.]? ?(o|0)[-. \/\\]? *-?((box|bin)|b|(#|n|num|number)?\d+))|(p(ost|ostal)? *(o(ff(ice)?)?)? *((box|bin)|b)? *(#|n|num|number)*\d+)|(p *-?\/?(o)? *-?box)|post office box|((box|bin)|b) *(#|n|num|number)? *\d+|(#|n|num|number) *\d+)/i;

const useValidateCustomerInfoPage = (cart: ICartProps): CartWorkflowStepValidationResults => {
    const customerInfoValidationResults = validateCustomerInfoForm(cart);
    if (!customerInfoValidationResults.isValid) return customerInfoValidationResults;

    const billingInfoValidationResults = validateBillingInfoForm(cart);
    if (!billingInfoValidationResults.isValid) return billingInfoValidationResults;

    const alternateContactValidationResults = validateAlternateContact(cart);
    if (!alternateContactValidationResults.isValid) return alternateContactValidationResults;

    const creditCheckValidationResults = validateCreditCheck(cart);
    if (!creditCheckValidationResults.isValid) return creditCheckValidationResults;

    return DefaultCartWorkflowStepValidationResults();
};

export const validateCustomerInfoForm = (cart: ICartProps): CartWorkflowStepValidationResults => {
    const results = DefaultCartWorkflowStepValidationResults();
    const customerInfo = cart.customerInfo;

    if (customerInfo.dob != '' && customerInfo.address.state != '') {
        const ageGateValue = customerInfo.address.state === 'Nebraska' ? 19 : 18;

        const validPurchaseDate = new Date();
        validPurchaseDate.setFullYear(validPurchaseDate.getFullYear() - ageGateValue);
        if (validPurchaseDate < new Date(customerInfo.dob)!) {
            results.messages.push(
                `You must be ${ageGateValue} years or older to make a purchase through the Viaero website.`
            );
            results.isValid = false;
            return results;
        }
    }

    if (
        customerInfo.firstName.trim() === '' ||
        customerInfo.lastName.trim() === '' ||
        customerInfo.address.addressLine1.trim() === '' ||
        customerInfo.address.city.trim() === '' ||
        customerInfo.address.state.trim() === '' ||
        customerInfo.address.zipcode.trim() === '' ||
        !zipcodeRegex.test(customerInfo.address.zipcode) ||
        customerInfo.emailAddress.trim() === '' ||
        !emailAddressRegex.test(customerInfo.emailAddress) ||
        customerInfo.phoneNumber.trim() === '' ||
        !phoneNumberRegex.test(customerInfo.phoneNumber) ||
        customerInfo.dob.trim() === '' ||
        customerInfo.driversLicenseState === '' ||
        customerInfo.driversLicenseNumber.trim() === '' ||
        customerInfo.employer.trim() === ''
    ) {
        results.messages.push('Please make sure all Customer Info fields are properly completed.');
        results.isValid = false;
        return results;
    }

    if (poBoxRegex.test(customerInfo.address.addressLine1)) {
        results.messages.push('PO Box Addresses are not permitted, a street address is required.');
        results.isValid = false;
        return results;
    }

    return results;
};

export const validateBillingInfoForm = (cart: ICartProps): CartWorkflowStepValidationResults => {
    const results = DefaultCartWorkflowStepValidationResults();

    if (!cart.customerInfo.isDifferentBillingAddress) return results;

    const billingInfo = cart.customerInfo.billingAddress;

    if (billingInfo && billingInfo?.dob != '' && billingInfo?.address.state != '') {
        const ageGateValue = billingInfo?.address.state === 'Nebraska' ? 19 : 18;

        const validPurchaseDate = new Date();
        validPurchaseDate.setFullYear(validPurchaseDate.getFullYear() - ageGateValue);
        if (validPurchaseDate < new Date(billingInfo.dob)!) {
            results.messages.push(
                `You must be ${ageGateValue} years or older to make a purchase through the Viaero website.`
            );
            results.isValid = false;
            return results;
        }
    }

    if (
        billingInfo?.firstName === '' ||
        billingInfo?.lastName === '' ||
        billingInfo?.address.addressLine1.trim() === '' ||
        billingInfo?.address.city.trim() === '' ||
        billingInfo?.address.state.trim() === '' ||
        billingInfo?.address.zipcode.trim() === '' ||
        (billingInfo?.address.zipcode && !zipcodeRegex.test(billingInfo.address.zipcode)) ||
        billingInfo?.address.state === ''
    ) {
        results.messages.push('Please make sure all Billing fields are properly completed.');
        results.isValid = false;
        return results;
    }

    return results;
};

export const validateAlternateContact = (cart: ICartProps): CartWorkflowStepValidationResults => {
    const results = DefaultCartWorkflowStepValidationResults();

    const alternateContact = cart.alternateContact;

    if (
        alternateContact?.firstName.trim() === '' ||
        alternateContact?.lastName.trim() === '' ||
        alternateContact?.address.addressLine1.trim() === '' ||
        alternateContact?.address.city.trim() === '' ||
        alternateContact?.address.state.trim() === '' ||
        alternateContact?.address.zipcode.trim() === '' ||
        (alternateContact?.address.zipcode &&
            !zipcodeRegex.test(alternateContact.address.zipcode)) ||
        alternateContact?.address.state === ''
    ) {
        results.messages.push(
            'Please make sure all Alternate Contact fields are properly completed.'
        );
        results.isValid = false;
        return results;
    }

    return results;
};

const validateCreditCheck = (cart: ICartProps): CartWorkflowStepValidationResults => {
    const results = DefaultCartWorkflowStepValidationResults();
    const creditCheckInformation = cart.agreements.creditCheckInformation;

    if (!creditCheckInformation.passedCreditCheck && creditCheckInformation.creditChecked) {
        results.messages.push('We will get in touch soon to continue your order.');
        results.isValid = false;
    }
    if (!creditCheckInformation.creditChecked) {
        results.messages.push('Please complete the credit check to continue your order');
        results.isValid = false;
    }

    return results;
};

export default useValidateCustomerInfoPage;
